/**
 * EntitiesListing component.
 * @module components/View/EntitiesListing
 */
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { BodyClass, hasBlocksData, flattenToAppURL } from '@plone/volto/helpers';
import { PreviewImage, Pagination } from '@plone/volto/components';
import { searchContent, getVocabulary } from '@plone/volto/actions';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { Grid, Label, Form, Header, Loader } from 'semantic-ui-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; 
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useDebounce } from "use-debounce";
import { isEmpty, map } from 'lodash'; 


const messages = defineMessages({
  labelText: {
    id: 'By words',
    defaultMessage: 'By words',
  },
  placeholderText: {
    id: 'What are you looking for?',
    defaultMessage: 'What are you looking for?',
  },
  defaultOptionEntityScopes: {
    id: 'All entity scopes',
    defaultMessage: 'All entity scopes',
  },
  labelEntityScope: {
    id: 'By category',
    defaultMessage: 'By category',
  },
  placeholderAllEntityScopes: {
    id: 'All entity scopes',
    defaultMessage: 'All entity scopes',
  },
  categoriesTitle: {
    id: 'Type',
    defaultMessage: 'Type',
  },
});


const EntitiesListing = (props) => {
  const { content } = props;
  const lang = content.language.token;
  const dispatch = useDispatch();
  const intl = useIntl();
  const [isInitialized, setIsInitialized] = useState(false);
  const entities = useSelector((state) => state.search.subrequests.entities?.items);
  const total = useSelector((state) => state.search.subrequests.entities?.total);
  const isLoading = useSelector((state) => state.search.subrequests.entities?.loading);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [search, setSearch] = useState(0);
  const [searchableTextInput, setSearchableText] = useState('');
  const [searchableText] = useDebounce(searchableTextInput, 500);
  const [entityScope, setEntityScope] = useState('');

  const vocabularyEntityScopes = 'product.palafolls.vocabularies.EntityScopes';
  const entityScopeData = useSelector(
    (state) => state.vocabularies[vocabularyEntityScopes]?.items ?? [],
    shallowEqual,
  );
  const entityScopeItems = useMemo(() => [
    { value: '', label: intl.formatMessage(messages.defaultOptionEntityScopes) },
    ...entityScopeData,
  ], [entityScopeData]);

  const getEntityScope = function (entityScope) {
    if (!entityScopeItems) {
      return entityScope
    }
    for (let item of entityScopeItems) {
      if (item.value === entityScope) return item.label
    }
    return entityScope
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const categoryParam = urlParams.get('category');
    if (categoryParam) {
        setEntityScope(categoryParam);
    }
    setIsInitialized(true);
}, []);

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: vocabularyEntityScopes, lang: content.language.token }));
  }, [dispatch]);

  useEffect(() => {
    if (!isInitialized) return;
    let contentFilter = {
      portal_type: ['Entity'],
      b_size: pageSize,
      b_start: currentPage * pageSize,
      review_state: 'published',
      SearchableText: searchableText,
      sort_on: 'getObjPositionInParent',
      metadata_fields: ['effective', 'entity_scopes', 'hasPreviewImage', 'featured', 'geolocation']
    };
    
    if (entityScope && entityScope !== '') {
      contentFilter.entity_scopes = entityScope;
    }

    dispatch(searchContent(flattenToAppURL(content['@id']), contentFilter, 'entities'));
  }, [dispatch, searchableText, entityScope, search, currentPage, pageSize, isInitialized]);

  useEffect(() => {
    const searchForm = document.querySelector('.searchList');
    const searchFormPos = searchForm ? searchForm.offsetTop : 0;
    let scrollPos = window.scrollY;
    if (scrollPos > searchFormPos && searchForm) {
      window.scrollTo({ top: searchFormPos })
    }
  }, [currentPage]);

	const handleSubjectClick = (event, subjectValue) => {
    event.preventDefault();
    event.stopPropagation();
    setEntityScope(subjectValue);
    setCurrentPage(0);
	};

  const onChangePage = (event, activePage) => {
    event.preventDefault();
    setCurrentPage(activePage.value);
  };

  const onChangePageSize = (event) => {
    event.preventDefault();
    setPageSize(parseInt(event.target.text));
    setCurrentPage(0);
  };

  const onChangeSearchableText = (event, data) => {
    event.preventDefault();
    setSearchableText(data.value);
    setCurrentPage(0);
  };

  const onChangeEntityScope = (event, data) => {
    event.preventDefault();
    setEntityScope(data.value);
    setCurrentPage(0);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setSearch(search + 1);
    setCurrentPage(0);
  };

  return (
    <>
      <BodyClass className='entities-list-view' />

      {hasBlocksData(content) ? (
        <>
          <RenderBlocks {...props} />
        </>
      ) : null} 
            
      <Form className="searchList" onSubmit={onSubmit}>
        <Header as="h2" className='justify-content-center searchList__title' size='small' textAlign='center'>
          <FormattedMessage id="Filter" defaultMessage="Filter" />
        </Header>
        <Form.Group widths="equal" className="searchList__formGroup align-items-end">
          <Form.Input
            id="textInputId"
            fluid
            label={intl.formatMessage(messages.labelText)}
            focus
            name="searchableText"
            value={searchableTextInput}
            onChange={onChangeSearchableText}
            placeholder={intl.formatMessage(messages.placeholderText)}
            type="text"
          />
          <Form.Select
            id="entityScopeSelectId"
            fluid
            label={intl.formatMessage(messages.labelEntityScope)}
            options={map(entityScopeItems, (item, index) => {
              return {
                key: index,
                value: item.value,
                text: item.label,
              };
            })}
            name="entityScope"
            value={entityScope}
            onChange={onChangeEntityScope}
            placeholder={intl.formatMessage(messages.placeholderAllEntityScopes)}
          />

        </Form.Group>
      </Form>

      {isLoading ? (
        <div className='mt-4'><Loader active inline="centered" size="large" content={intl.formatMessage({ id: 'Loading...', defaultMessage: 'Loading...' })} /></div>
      ) : (
        <>
        {!isEmpty(entities) ? (
          <>
          <Grid className='mt-4'>
            <Grid.Row as='ul' columns={12} className='pt-5 list-unstyled my-0 entityList__list'>
                {entities.map((item) => (
                <Grid.Column as='li' className='mb-4' mobile={12} tablet={6} computer={4} key={item['@id']}>

                  <article className='entityListItem position-relative'>

                    {item.image_field && (
                      <PreviewImage
                        className="entityListItem__img"
                        item={item}
                        alt=''
                        sizes="(max-width: 767px) 100vw, (max-width: 991px) 50vw, 33.33vw"
                      />
                    )}

                    <div className="entityListItem__text">

                      {item?.entity_scopes ? (
                        <div aria-label={intl.formatMessage(messages.categoriesTitle)}>
                          <ul className="mb-3 list-unstyled flex-wrap d-flex gap-2 lh-1 fs-xs" >
                            {map(item.entity_scopes, (item) => (
                              <li className='entityListItem__type' key={item}>
                                <a href={`?category=${encodeURIComponent(item)}`} onClick={(e) => handleSubjectClick(e, item)}
                                  className="entityListItem__category subjectLabel">{getEntityScope(item)}</a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}

                      <h2 className="entityListItem__title">
                        <a href={flattenToAppURL(item['@id'])} className="entityListItem__link stretched-link ">{item.title}</a>
                      </h2>

                      {item.description && (<p className="entityListItem__desc">{item.description}</p>)}

                      <div className="text-end mt-auto">
                        <FontAwesomeIcon icon={faArrowRight} />
                      </div>
                    </div>
                  </article>

                </Grid.Column>
                ))}
            </Grid.Row>
          </Grid>
          {total > pageSize && (
            <nav className="pagination">
              <Pagination
                current={currentPage}
                total={Math.ceil(total / pageSize)}
                onChangePage={onChangePage}
                onChangePageSize={onChangePageSize}
              />
            </nav>
          )}
          </>
          ) : (
          <div className="pt-5 my-0">
            <p><FormattedMessage id="No entities or associations found" defaultMessage="No entities or associations found" /></p>
          </div>
        )}
        </>
      )}

    </>
  );
};

export default EntitiesListing;